import React, { useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/RichTextEditor.css';

const textColors = [
  '#000000', '#FFFFFF', '#FF5733', '#33FF57', '#3357FF',
  '#FF33F5', '#33F5FF', '#FFD700', '#FF8C00', '#DC143C',
  '#228B22', '#00008B', '#8B008B', '#696969', '#FF1493'
];

const highlightColors = [
  '#FFFACD', '#FFFFE0', '#FFF0F5', '#F0FFFF', '#F5F5DC',
  '#FFFAF0', '#F0FFF0', '#F5FFFA', '#FFF8DC', '#FFE4E1'
];

const Font = Quill.import('formats/font');
Font.whitelist = [
  'arial',
  'georgia', 
  'times-new-roman',
  'courier-new',
  'verdana',
  'helvetica',
  'trebuchet-ms',
  'impact',
  'roboto'
];
Quill.register(Font, true);

const Parchment = Quill.import('parchment');
const fontNameMap = {
  'arial': 'Arial',
  'georgia': 'Georgia',
  'times-new-roman': 'Times New Roman',
  'courier-new': 'Courier New',
  'verdana': 'Verdana',
  'helvetica': 'Helvetica',
  'trebuchet-ms': 'Trebuchet MS',
  'impact': 'Impact',
  'roboto': 'Roboto'
};

const ToolbarClass = Quill.import('modules/toolbar');
ToolbarClass.DEFAULTS.handlers.font = function(value) {
  if (value === 'false') {
    this.quill.removeFormat('font');
  } else {
    this.quill.format('font', value);
  }
};

const Size = Quill.import('formats/size');
Size.whitelist = ['small', false, 'large', 'huge'];
Quill.register(Size, true);

const Color = Quill.import('formats/color');
Color.whitelist = textColors;
Quill.register(Color, true);

const BackgroundColor = Quill.import('formats/background');
BackgroundColor.whitelist = highlightColors;
Quill.register(BackgroundColor, true);

const RichTextEditor = React.forwardRef(({ value, onChange, placeholder = "Write your content here..." }, ref) => {
  const quillRef = React.useRef(null);

  useEffect(() => {
    if (quillRef.current && value !== undefined) {
      const editor = quillRef.current.getEditor();
      if (editor && value !== editor.root.innerHTML) {
        editor.root.innerHTML = value;
      }
    }
  }, [value]);

  useEffect(() => {
    // Apply font styles to dropdown after Quill renders
    const applyFontStyles = () => {
      const fontOptions = document.querySelectorAll('.ql-font .ql-picker-item');
      const fontLabels = {
        'arial': { name: 'Arial', family: 'Arial, sans-serif' },
        'georgia': { name: 'Georgia', family: 'Georgia, serif' },
        'times-new-roman': { name: 'Times New Roman', family: '"Times New Roman", Times, serif' },
        'courier-new': { name: 'Courier New', family: '"Courier New", Courier, monospace' },
        'verdana': { name: 'Verdana', family: 'Verdana, sans-serif' },
        'helvetica': { name: 'Helvetica', family: 'Helvetica, Arial, sans-serif' },
        'trebuchet-ms': { name: 'Trebuchet MS', family: '"Trebuchet MS", sans-serif' },
        'impact': { name: 'Impact', family: 'Impact, sans-serif' },
        'roboto': { name: 'Roboto', family: 'Roboto, sans-serif' }
      };

      fontOptions.forEach(option => {
        const fontValue = option.getAttribute('data-value');
        if (fontLabels[fontValue]) {
          option.style.fontFamily = fontLabels[fontValue].family;
          option.innerHTML = '';
          option.appendChild(document.createTextNode(fontLabels[fontValue].name));
        }
      });
    };

    const timer = setTimeout(applyFontStyles, 50);
    return () => clearTimeout(timer);
  }, []);

  React.useImperativeHandle(ref, () => ({
    getEditor: () => {
      return quillRef.current ? quillRef.current.getEditor() : null;
    }
  }));

  const modules = {
    toolbar: [
      [{ 'font': Font.whitelist }],
      [{ 'size': Size.whitelist }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': textColors }, { 'background': highlightColors }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ]
  };

  const formats = [
    'font',
    'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'blockquote', 'code-block',
    'list',
    'indent',
    'link',
    'image',
    'video'
  ];

  const handleChange = (content) => {
    onChange(content);
  };

  return (
    <div className="rich-text-editor-container">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
